// src/components/apps/Licenses/LicensesTable.js
import React, { useMemo, useState } from 'react';
import {
  Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, CircularProgress, IconButton,
  Select, MenuItem, FormControl, InputLabel, Box, Button, Chip, Tooltip,
  TablePagination, Switch, TextField, ToggleButton, ToggleButtonGroup
} from '@mui/material';
import { Delete, Edit } from '@mui/icons-material';
import UploadLicensesCSV from './UploadLicensesCSV';
import { API_ENDPOINTS } from '../../../constants/apiEndpoints';
import { useAuth } from '../../../context/AuthContext';

/** Reusable expandable text cell (click to expand/collapse) — JS only */
const ExpandableCell = ({ value, maxWidth = 200 }) => {
  const [expanded, setExpanded] = useState(false);
  if (!value) return <Box component="span">—</Box>;
  return (
    <Box
      sx={{
        maxWidth,
        whiteSpace: expanded ? 'normal' : 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        cursor: 'pointer',
      }}
      onClick={() => setExpanded(prev => !prev)}
    >
      <Tooltip title="Click to expand/collapse" arrow>
        <Box component="span">{value}</Box>
      </Tooltip>
    </Box>
  );
};

// Expiration status buckets: ordered most→least urgent, with shared colors.
const EXP_ORDER = ['expired', '<30 days', '<60 days', '<90 days', '>90 days'];
const EXP_COLORS = {
  expired:    '#616161',
  '<30 days': '#d32f2f',
  '<60 days': '#f57c00',
  '<90 days': '#1976d2',
  '>90 days': '#388e3c',
};
const expRank = (s) => {
  const i = EXP_ORDER.indexOf(s);
  return i === -1 ? EXP_ORDER.length : i; // unknown/blank sort last
};

// helper: YYYYMMDD_HHMMSS (local time, zero‑padded)
const stamp = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return (
    d.getFullYear() + pad(d.getMonth() + 1) + pad(d.getDate()) + '_' +
    pad(d.getHours()) + pad(d.getMinutes()) + pad(d.getSeconds())
  );
};

const LicensesTable = ({
  licenses = [],
  loading = false,
  reload,
  onEdit,
  onDelete,
  onLocalToggle, // parent-provided optimistic updater (so Matrix updates instantly)
}) => {
  const { apiFetch } = useAuth();
  const [filters, setFilters] = useState({
    site: '',
    category: '',
    department: '',
    description: '',
    softwareText: '',
    vendor: '',
    versionText: '',
    webapp: '',           // '', 'true', 'false'
    ready_to_install: '', // '', 'true', 'false'
    packaged: '',         // '', 'true', 'false'
  });
  const [expirationWindow, setExpirationWindow] = useState('');
  const [expStatusFilter, setExpStatusFilter] = useState([]); // multi-select toggles
  const [sortField, setSortField] = useState('expiration_status');
  const [sortOrder, setSortOrder] = useState('asc');

  // pagination (user‑selectable; -1 = Show All)
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // saving state per cell: keys like `${id}.ready_to_install` or `${id}.version`
  const [saving, setSaving] = useState({});

  // local overrides for inline version edits: { [id]: string }
  const [versionOverrides, setVersionOverrides] = useState({});

  const handleFilterChange = (field) => (event) => {
    setFilters(prev => ({ ...prev, [field]: event.target.value }));
    setPage(0);
  };

  const formatDate = (dateStr) => (dateStr ? new Date(dateStr).toLocaleDateString('en-US') : '');

  // effective version (use override if present)
  const getVersion = (l) => {
    const v = versionOverrides[l.id];
    return v !== undefined ? v : (l.version || '');
  };

  const filteredLicenses = useMemo(() => {
    const now = new Date();
    const swQuery = (filters.softwareText || '').toLowerCase().trim();
    const verQuery = (filters.versionText || '').toLowerCase().trim();

    const arr = (licenses || []).map(l => ({
      ...l,
      __version: getVersion(l),
    }));

    return arr
      .filter(l =>
        (!filters.site || l.site === filters.site) &&
        (!filters.category || l.category === filters.category) &&
        (!filters.department || l.department === filters.department) &&
        (!filters.vendor || l.vendor === filters.vendor) &&
        (!filters.description || (l.description || '').toLowerCase().includes(filters.description.toLowerCase())) &&
        (!swQuery || (l.software || '').toLowerCase().includes(swQuery)) &&
        (!verQuery || (l.__version || '').toLowerCase().includes(verQuery)) &&
        (filters.webapp === '' ||
          (filters.webapp === 'true' ? !!l.webapp : !l.webapp)) &&
        (filters.ready_to_install === '' ||
          (filters.ready_to_install === 'true' ? !!l.ready_to_install : !l.ready_to_install)) &&
        (filters.packaged === '' ||
          (filters.packaged === 'true' ? !!l.packaged : !l.packaged))
      )
      .filter(l =>
        expStatusFilter.length === 0 || expStatusFilter.includes(l.expiration_status)
      )
      .filter(l => {
        if (!expirationWindow) return true;
        const dueDate = new Date(l.date_due);
        const daysOut = parseInt(expirationWindow, 10);
        const futureDate = new Date();
        futureDate.setDate(now.getDate() + daysOut);
        return dueDate >= now && dueDate <= futureDate;
      })
      .sort((a, b) => {
        let aVal = a[sortField];
        let bVal = b[sortField];

        if (sortField === 'version') {
          aVal = (a.__version || '').toLowerCase();
          bVal = (b.__version || '').toLowerCase();
        } else if (sortField === 'expiration_status') {
          aVal = expRank(a.expiration_status);
          bVal = expRank(b.expiration_status);
        } else if (['date_due', 'date_received', 'renewed_date'].includes(sortField)) {
          aVal = new Date(aVal);
          bVal = new Date(bVal);
        }

        if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
        return 0;
      });
  }, [licenses, filters, expirationWindow, expStatusFilter, sortField, sortOrder, versionOverrides]);

  // Counts per expiration bucket (from the full set) for the toggle labels.
  const expCounts = useMemo(() => {
    const m = {};
    (licenses || []).forEach(l => {
      const k = l.expiration_status;
      if (k) m[k] = (m[k] || 0) + 1;
    });
    return m;
  }, [licenses]);

  const expStatusesPresent = useMemo(
    () => EXP_ORDER.filter(s => expCounts[s]),
    [expCounts]
  );

  const pagedLicenses = useMemo(() => {
    if (rowsPerPage === -1) return filteredLicenses; // Show All
    const start = page * rowsPerPage;
    return filteredLicenses.slice(start, start + rowsPerPage);
  }, [filteredLicenses, page, rowsPerPage]);

  // keep page in bounds if filters change
  const maxPage = rowsPerPage === -1
    ? 0
    : Math.max(0, Math.ceil(filteredLicenses.length / rowsPerPage) - 1);
  if (page > maxPage && maxPage !== page) setPage(0);

  const getUniqueOptions = (key) => {
    const values = (licenses || []).map(item => item[key]).filter(Boolean);
    return [...new Set(values)].sort((a, b) => a.localeCompare(b));
  };

  const exportToCSV = () => {
    const headers = [
      'Expiration Status', 'Status','Date Received', 'Date Due', 'Site',
      'Version', 'Webapp', 'Ready To Install', 'Packaged',
      'Site POC', 'Site POC Email', 'Category',
      'Support Department', 'Software', 'Description', 'Vendor', 'Vendor POC', 'Vendor POC Email','Vendor Quote',
      'Total', 'GL Account', 'Renewed Date', 'Notes'
    ];
    const rows = filteredLicenses.map(l => [
      l.expiration_status, l.status, l.date_received, l.date_due, l.site,
      l.__version || '',
      l.webapp ? 'true' : 'false',
      l.ready_to_install ? 'true' : 'false',
      l.packaged ? 'true' : 'false',
      l.site_poc, l.site_poc_email, l.category, l.department, l.software, l.description, l.vendor,
      l.vendor_poc, l.vendor_poc_email, l.vendor_quote,
      l.total, l.gl_account, l.renewed_date, l.notes
    ]);

    const csvContent = 'data:text/csv;charset=utf-8,' +
      [headers, ...rows].map(e => e.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');

    const blob = new Blob([decodeURIComponent(encodeURI(csvContent))], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `app_export_${stamp()}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const toggleField = async (license, field) => {
    const key = `${license.id}.${field}`;
    const current = !!license[field];
    const next = !current;

    onLocalToggle?.(license.id, field, next); // optimistic (parent)
    setSaving(prev => ({ ...prev, [key]: true }));

    try {
      await apiFetch(`${API_ENDPOINTS.SOFTWARE_LICENSES}${license.id}/`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ [field]: next }),
      });
    } catch (e) {
      console.error('Toggle failed:', e);
      onLocalToggle?.(license.id, field, current); // rollback
      alert('Failed to update. Please try again.');
    } finally {
      setSaving(prev => ({ ...prev, [key]: false }));
    }
  };

  // --- Inline version editing ---
  const onChangeVersion = (id, val) => {
    setVersionOverrides(prev => ({ ...prev, [id]: val }));
  };

  const saveVersion = async (license) => {
    const key = `${license.id}.version`;
    const next = (versionOverrides[license.id] ?? license.version ?? '').trim();
    const current = (license.version ?? '').trim();

    // nothing changed
    if (next === current) return;

    // optimistic to parent if it handles non-boolean updates
    onLocalToggle?.(license.id, 'version', next);

    setSaving(prev => ({ ...prev, [key]: true }));
    try {
      await apiFetch(`${API_ENDPOINTS.SOFTWARE_LICENSES}${license.id}/`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ version: next }),
      });
      // keep override so UI reflects what user typed; parent may replace with server value later
    } catch (e) {
      console.error('Version update failed:', e);
      alert('Failed to update version.');
      // rollback local override to server value
      setVersionOverrides(prev => {
        const clone = { ...prev };
        clone[license.id] = current;
        return clone;
      });
    } finally {
      setSaving(prev => ({ ...prev, [key]: false }));
    }
  };

  if (loading) return <CircularProgress />;

  // Headers (Version + Webapp immediately after Site; then Ready/Packaged)
  const headerLabels = [
    'Actions','Status', 'Expiration Status',
    'Software', 'Category', 'Support Department', 'Description', 'Site',
    'Version', 'Webapp', 'Ready to Install', 'Packaged',
    'Site POC', 'Site POC Email',
    'Vendor', 'Vendor POC', 'Vendor POC Email', 'Vendor Quote',
    'Total', 'GL Account',
    'Date Received', 'Date Due', 'Renewed Date',
    'Notes'
  ];

  const labelFor = (field) =>
  field === 'department'
    ? 'Support Department'
    : field.charAt(0).toUpperCase() + field.slice(1);

  return (
    <>
      {/* Filters */}
      <Box display="flex" gap={2} flexWrap="wrap" mb={2} alignItems="center">
        {/* Selects */}
        {['site', 'category', 'department'].map((field) => (
          <FormControl key={field} sx={{ minWidth: 180 }} size="small">
            <InputLabel>{labelFor(field)}</InputLabel>
            <Select
              value={filters[field]}
              onChange={handleFilterChange(field)}
              label={labelFor(field)}
            >
              <MenuItem value="">All</MenuItem>
              {getUniqueOptions(field).map(option => (
                <MenuItem key={option} value={option}>{option}</MenuItem>
              ))}
            </Select>
          </FormControl>
        ))}

        {/* Software & Version text filters */}
        <TextField
          size="small"
          sx={{ minWidth: 220 }}
          label="Software (contains)"
          value={filters.softwareText}
          onChange={(e) => handleFilterChange('softwareText')({ target: { value: e.target.value } })}
        />
        <TextField
          size="small"
          sx={{ minWidth: 180 }}
          label="Version (contains)"
          value={filters.versionText}
          onChange={(e) => handleFilterChange('versionText')({ target: { value: e.target.value } })}
        />

        {/* Description & Vendor selects */}
        {['description', 'vendor'].map((field) => (
          <FormControl key={field} sx={{ minWidth: 180 }} size="small">
            <InputLabel>{field.charAt(0).toUpperCase() + field.slice(1)}</InputLabel>
            <Select
              value={filters[field]}
              onChange={handleFilterChange(field)}
              label={field.charAt(0).toUpperCase() + field.slice(1)}
            >
              <MenuItem value="">All</MenuItem>
              {getUniqueOptions(field).map(option => (
                <MenuItem key={option} value={option}>{option}</MenuItem>
              ))}
            </Select>
          </FormControl>
        ))}

        {/* Boolean filters */}
        <FormControl sx={{ minWidth: 160 }} size="small">
          <InputLabel>Webapp</InputLabel>
          <Select
            value={filters.webapp}
            onChange={handleFilterChange('webapp')}
            label="Webapp"
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="true">Yes</MenuItem>
            <MenuItem value="false">No</MenuItem>
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 180 }} size="small">
          <InputLabel>Ready to Install</InputLabel>
          <Select
            value={filters.ready_to_install}
            onChange={handleFilterChange('ready_to_install')}
            label="Ready to Install"
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="true">Yes</MenuItem>
            <MenuItem value="false">No</MenuItem>
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 160 }} size="small">
          <InputLabel>Packaged</InputLabel>
          <Select
            value={filters.packaged}
            onChange={handleFilterChange('packaged')}
            label="Packaged"
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="true">Yes</MenuItem>
            <MenuItem value="false">No</MenuItem>
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 160 }} size="small">
          <InputLabel>Expiring In</InputLabel>
          <Select
            value={expirationWindow}
            onChange={(e) => { setExpirationWindow(e.target.value); setPage(0); }}
            label="Expiring In"
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="30">Next 30 Days</MenuItem>
            <MenuItem value="60">Next 60 Days</MenuItem>
            <MenuItem value="90">Next 90 Days</MenuItem>
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 160 }} size="small">
          <InputLabel>Sort By</InputLabel>
          <Select
            value={sortField}
            onChange={(e) => { setSortField(e.target.value); setPage(0); }}
            label="Sort By"
          >
            <MenuItem value="date_due">Date Due</MenuItem>
            <MenuItem value="expiration_status">Expiration Status</MenuItem>
            <MenuItem value="date_received">Date Received</MenuItem>
            <MenuItem value="renewed_date">Renewed Date</MenuItem>
            <MenuItem value="total">Total</MenuItem>
            <MenuItem value="site">Site</MenuItem>
            <MenuItem value="category">Category</MenuItem>
            <MenuItem value="department">Support Department</MenuItem>
            <MenuItem value="vendor">Vendor</MenuItem>
            <MenuItem value="version">Version</MenuItem>
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 120 }} size="small">
          <InputLabel>Order</InputLabel>
          <Select
            value={sortOrder}
            onChange={(e) => { setSortOrder(e.target.value); setPage(0); }}
            label="Order"
          >
            <MenuItem value="asc">Ascending</MenuItem>
            <MenuItem value="desc">Descending</MenuItem>
          </Select>
        </FormControl>

        <Button variant="outlined" onClick={exportToCSV}>
          Download CSV
        </Button>

        {/* CSV uploader; refresh table after success via parent reload */}
        <UploadLicensesCSV onDone={() => reload && reload()} />
      </Box>

      {/* Expiration status toggles (multi-select filter) */}
      {expStatusesPresent.length > 0 && (
        <Box display="flex" gap={1} alignItems="center" flexWrap="wrap" mb={2}>
          <Box component="span" sx={{ fontWeight: 600, mr: 1 }}>Expiration:</Box>
          <ToggleButtonGroup
            value={expStatusFilter}
            onChange={(_, val) => { setExpStatusFilter(val); setPage(0); }}
            size="small"
            sx={{ flexWrap: 'wrap' }}
          >
            {expStatusesPresent.map((s) => (
              <ToggleButton
                key={s}
                value={s}
                sx={{
                  textTransform: 'none',
                  color: EXP_COLORS[s],
                  borderColor: EXP_COLORS[s],
                  '&.Mui-selected': {
                    backgroundColor: EXP_COLORS[s],
                    color: '#fff',
                    '&:hover': { backgroundColor: EXP_COLORS[s] },
                  },
                }}
              >
                {s} ({expCounts[s]})
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          {expStatusFilter.length > 0 && (
            <Button size="small" onClick={() => { setExpStatusFilter([]); setPage(0); }}>
              Clear
            </Button>
          )}
        </Box>
      )}

      {/* Totals */}
      <Box sx={{
        backgroundColor: '#f1f1f1',
        p: 2,
        borderRadius: 1,
        mb: 2,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <strong>Total Apps: {filteredLicenses.length}</strong>
        <strong>
          Total Cost: $
          {filteredLicenses.reduce((sum, item) => sum + (parseFloat(item.total) || 0), 0).toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          })}
        </strong>
      </Box>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table
          size="small"
          stickyHeader
          sx={{ '& td, & th': { py: 0.5, px: 1 } }}
        >
          <TableHead>
            <TableRow>
              {headerLabels.map((label) => (
                <TableCell
                  key={label}
                  sx={{ backgroundColor: '#1f3e76', color: 'white', fontWeight: 'bold' }}
                  align={label === 'Actions' ? 'center' : 'left'}
                >
                  {label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {pagedLicenses.length === 0 ? (
              <TableRow>
                <TableCell colSpan={headerLabels.length} align="center">No Apps found.</TableCell>
              </TableRow>
            ) : (
              pagedLicenses.map((license, index) => {
                const disabledByWebapp = !!license.webapp;
                const versionKey = `${license.id}.version`;
                const versionVal = getVersion(license);

                return (
                  <TableRow key={license.id} sx={{ backgroundColor: index % 2 === 0 ? '#fafafa' : 'white' }}>
                    {/* Actions */}
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                        <IconButton size="small" onClick={() => onEdit(license)}>
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => onDelete(license.id)}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>

                    {/* Status */}
                    <TableCell sx={{
                      color: {
                        Quoting: 'blue',
                        Processing: 'orange',
                        Purchased: 'purple',
                        Complete: 'green',
                        'Not Started': 'gray',
                        Other: 'black'
                      }[license.status] || 'inherit',
                      fontWeight: 500
                    }}>
                      <ExpandableCell value={license.status} maxWidth={160} />
                    </TableCell>

                    {/* Expiration Status */}
                    <TableCell>
                      {license.expiration_status && (
                        <Chip
                          label={license.expiration_status}
                          size="small"
                          variant="outlined"
                          sx={{
                            backgroundColor: EXP_COLORS[license.expiration_status] || '#e0e0e0',
                            color: 'white',
                            fontWeight: 'bold',
                          }}
                        />
                      )}
                    </TableCell>

                    {/* Text fields */}
                    <TableCell><ExpandableCell value={license.software} /></TableCell>
                    <TableCell><ExpandableCell value={license.category} /></TableCell>
                    <TableCell><ExpandableCell value={license.department} /></TableCell>
                    <TableCell><ExpandableCell value={license.description} maxWidth={200} /></TableCell>
                    <TableCell><ExpandableCell value={license.site} /></TableCell>

                    {/* Version (inline editable) */}
                    <TableCell sx={{ minWidth: 120, flex: 1 }}>
                      <TextField
                        size="small"
                        value={versionVal}
                        onChange={(e) => onChangeVersion(license.id, e.target.value)}
                        onBlur={() => saveVersion(license)}
                        onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                        placeholder="e.g. 12.3.1"
                        disabled={!!saving[versionKey]}
                        slotProps={{
                          htmlInput: { 'aria-label': 'Version' } // put any native input attrs here
                        }}
                        fullWidth
                      />
                    </TableCell>

                    {/* Webapp toggle */}
                    <TableCell align="center">
                      <Switch
                        size="small"
                        checked={!!license.webapp}
                        onChange={() => toggleField(license, 'webapp')}
                        disabled={!!saving[`${license.id}.webapp`]}
                        inputProps={{ 'aria-label': 'Webapp' }}
                      />
                    </TableCell>

                    {/* Ready to Install (disabled if webapp true) */}
                    <TableCell align="center">
                      <Switch
                        size="small"
                        checked={!!license.ready_to_install}
                        onChange={() => toggleField(license, 'ready_to_install')}
                        disabled={disabledByWebapp || !!saving[`${license.id}.ready_to_install`]}
                        inputProps={{ 'aria-label': 'Ready to install' }}
                      />
                    </TableCell>

                    {/* Packaged (disabled if webapp true) */}
                    <TableCell align="center">
                      <Switch
                        size="small"
                        checked={!!license.packaged}
                        onChange={() => toggleField(license, 'packaged')}
                        disabled={disabledByWebapp || !!saving[`${license.id}.packaged`]}
                        inputProps={{ 'aria-label': 'Packaged' }}
                      />
                    </TableCell>

                    {/* Contacts / vendor */}
                    <TableCell><ExpandableCell value={license.site_poc} maxWidth={180} /></TableCell>
                    <TableCell><ExpandableCell value={license.site_poc_email} maxWidth={240} /></TableCell>
                    <TableCell><ExpandableCell value={license.vendor} /></TableCell>
                    <TableCell><ExpandableCell value={license.vendor_poc} maxWidth={180} /></TableCell>
                    <TableCell><ExpandableCell value={license.vendor_poc_email} maxWidth={240} /></TableCell>
                    <TableCell><ExpandableCell value={license.vendor_quote} maxWidth={280} /></TableCell>

                    {/* Numbers / codes */}
                    <TableCell>
                      ${parseFloat(license.total || 0).toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}
                    </TableCell>
                    <TableCell><ExpandableCell value={license.gl_account} maxWidth={140} /></TableCell>

                    {/* Dates */}
                    <TableCell>{formatDate(license.date_received)}</TableCell>
                    <TableCell>{formatDate(license.date_due)}</TableCell>
                    <TableCell>{formatDate(license.renewed_date)}</TableCell>

                    {/* Notes */}
                    <TableCell><ExpandableCell value={license.notes} maxWidth={220} /></TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>

        {/* Pagination with "All" */}
        <TablePagination
          component="div"
          count={filteredLicenses.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            const next = parseInt(e.target.value, 10);
            setRowsPerPage(next);
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100, { label: 'All', value: -1 }]}
        />
      </TableContainer>
    </>
  );
};

export default LicensesTable;